import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import articlesData from '../articles.json';

const ArticleContainer = styled.article`
  min-height: 100vh;
  background: white;
`;

const ArticleHero = styled.div`
  position: relative;
  height: 60vh;
  min-height: 400px;
  max-height: 600px;
  overflow: hidden;
  background: var(--neutral-900);

  @media (max-width: 768px) {
    height: 40vh;
    min-height: 300px;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.7;
`;

const HeroOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, 
    rgba(0,0,0,0.3) 0%, 
    rgba(0,0,0,0.6) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeroContent = styled.div`
  text-align: center;
  color: white;
  padding: var(--space-4);
  max-width: 900px;
  margin: 0 auto;
`;

const Category = styled.span`
  background: var(--primary-600);
  color: white;
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-full);
  font-size: var(--text-sm);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  display: inline-block;
  margin-bottom: var(--space-4);
`;

const ArticleTitle = styled.h1`
  font-size: var(--text-5xl);
  font-family: var(--font-serif);
  margin-bottom: var(--space-6);
  line-height: 1.2;
  color: white;

  @media (max-width: 768px) {
    font-size: var(--text-3xl);
  }
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-4);
  flex-wrap: wrap;
  font-size: var(--text-base);
  color: rgba(255, 255, 255, 0.9);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: var(--space-2);
`;

const ArticleContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-12) var(--space-4);

  @media (max-width: 768px) {
    padding: var(--space-8) var(--space-4);
  }
`;

const AuthorSection = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-4);
  padding: var(--space-6);
  background: var(--neutral-100);
  border-radius: var(--radius-lg);
  margin-bottom: var(--space-8);

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const AuthorImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: var(--radius-full);
  object-fit: cover;
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.h3`
  font-size: var(--text-lg);
  color: var(--neutral-900);
  margin-bottom: var(--space-1);
`;

const AuthorBio = styled.p`
  font-size: var(--text-sm);
  color: var(--neutral-700);
  margin: 0;
`;

const Section = styled.section`
  margin-bottom: var(--space-8);
`;

const SectionHeading = styled.h2`
  font-size: var(--text-2xl);
  color: var(--neutral-900);
  margin-bottom: var(--space-4);
  font-family: var(--font-serif);
  position: relative;
  padding-left: var(--space-4);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 100%;
    background: var(--primary-600);
    border-radius: var(--radius-sm);
  }
`;

const SectionBody = styled.div`
  font-size: var(--text-lg);
  line-height: 1.8;
  color: var(--neutral-800);

  p {
    margin-bottom: var(--space-4);
  }

  ul, ol {
    margin: var(--space-4) 0;
    padding-left: var(--space-8);

    li {
      margin-bottom: var(--space-2);
    }
  }

  blockquote {
    border-left: 4px solid var(--primary-400);
    padding-left: var(--space-4);
    margin: var(--space-6) 0;
    font-style: italic;
    color: var(--neutral-700);
  }

  strong {
    color: var(--neutral-900);
    font-weight: 600;
  }
`;

const TagsSection = styled.div`
  margin-top: var(--space-8);
  padding-top: var(--space-8);
  border-top: 1px solid var(--neutral-200);
`;

const TagsTitle = styled.h3`
  font-size: var(--text-lg);
  color: var(--neutral-900);
  margin-bottom: var(--space-4);
`;

const TagsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
`;

const Tag = styled.span`
  background: var(--neutral-200);
  color: var(--neutral-700);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-size: var(--text-sm);
  font-weight: 500;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--primary-100);
    color: var(--primary-700);
  }
`;

const SourcesSection = styled.div`
  margin-top: var(--space-8);
  padding: var(--space-6);
  background: white;
  border: 1px solid var(--neutral-200);
  border-radius: var(--radius-lg);
`;

const SourcesTitle = styled.h3`
  font-size: var(--text-lg);
  color: var(--neutral-900);
  margin-bottom: var(--space-4);
`;

const SourcesList = styled.ul`
  list-style: none;
  padding: 0;
`;

const SourceItem = styled.li`
  margin-bottom: var(--space-3);
`;

const SourceLink = styled.a`
  color: var(--primary-600);
  text-decoration: none;
  font-size: var(--text-base);
  display: flex;
  align-items: center;
  gap: var(--space-2);

  &:hover {
    color: var(--primary-700);
    text-decoration: underline;
  }

  &::before {
    content: '📎';
    font-size: var(--text-sm);
  }
`;

const NavigationSection = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: var(--space-8) var(--space-4);
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid var(--neutral-200);
`;

const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  background: var(--neutral-100);
  color: var(--neutral-700);
  padding: var(--space-3) var(--space-4);
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: all var(--transition-fast);

  &:hover {
    background: var(--neutral-200);
    color: var(--neutral-900);
    text-decoration: none;
  }

  &::before {
    content: '←';
    font-size: var(--text-lg);
  }
`;

const NotFoundContainer = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  padding: var(--space-8);

  h1 {
    font-size: var(--text-4xl);
    color: var(--neutral-700);
    margin-bottom: var(--space-4);
  }

  p {
    font-size: var(--text-xl);
    color: var(--neutral-600);
    margin-bottom: var(--space-8);
  }
`;

const ArticlePage = () => {
  const { id } = useParams();
  const articles = useMemo(() => articlesData.articles || [], []);
  
  const article = useMemo(() => {
    return articles.find(article => article.id === parseInt(id));
  }, [articles, id]);

  if (!article) {
    return (
      <ArticleContainer>
        <NotFoundContainer>
          <h1>Article Not Found</h1>
          <p>The article you're looking for doesn't exist.</p>
          <BackButton to="/">Back to Articles</BackButton>
        </NotFoundContainer>
      </ArticleContainer>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <ArticleContainer>
      <ArticleHero>
        <HeroImage src={article.image} alt={article.title} />
        <HeroOverlay>
          <HeroContent>
            <Category>{article.category}</Category>
            <ArticleTitle>{article.title}</ArticleTitle>
            <ArticleMeta>
              <MetaItem>📅 {formatDate(article.publishDate)}</MetaItem>
              <MetaItem>📖 {article.readingTime}</MetaItem>
              <MetaItem>✍️ {article.authorName}</MetaItem>
            </ArticleMeta>
          </HeroContent>
        </HeroOverlay>
      </ArticleHero>

      <ArticleContent>
        {article.authorImage && article.authorBio && (
          <AuthorSection>
            <AuthorImage src={article.authorImage} alt={article.authorName} />
            <AuthorInfo>
              <AuthorName>{article.authorName}</AuthorName>
              <AuthorBio>{article.authorBio}</AuthorBio>
            </AuthorInfo>
          </AuthorSection>
        )}

        {article.content.map((section, index) => (
          <Section key={index}>
            <SectionHeading>{section.heading}</SectionHeading>
            <SectionBody dangerouslySetInnerHTML={{ __html: section.body }} />
          </Section>
        ))}

        {article.tags && (
          <TagsSection>
            <TagsTitle>Related Topics</TagsTitle>
            <TagsList>
              {article.tags.map((tag, index) => (
                <Tag key={index}>{tag}</Tag>
              ))}
            </TagsList>
          </TagsSection>
        )}

        {article.sources && article.sources.length > 0 && (
          <SourcesSection>
            <SourcesTitle>References & Further Reading</SourcesTitle>
            <SourcesList>
              {article.sources.map((source, index) => (
                <SourceItem key={index}>
                  <SourceLink href={source.url} target="_blank" rel="noopener noreferrer">
                    {source.title}
                  </SourceLink>
                </SourceItem>
              ))}
            </SourcesList>
          </SourcesSection>
        )}
      </ArticleContent>

      <NavigationSection>
        <BackButton to="/">Back to Articles</BackButton>
      </NavigationSection>
    </ArticleContainer>
  );
};

export default ArticlePage;